/**
 * AssessmentInProgress (staff POW-fork step) — POW-1.
 *
 * Catalogue source (AUTHORITATIVE — do NOT edit there): the staff
 * Partners-drilldown `AssessmentInProgress` in
 * `portal-staff/src/routes/admin/BriefDetailPage.tsx`
 * (nx-reusable-component-registry.md → "Assessment-In-Progress"; Structure Guide v8 §9
 * Rule 3 — catalogue-and-fork). This is the §9 COPY-FORK for the staff-launched
 * POW OBW Step-7 poll state — the SAME trimmed shape the subscriber POW step
 * carries (`portal-subscriber/src/obw/steps/partner/AssessmentInProgress.tsx`):
 * the gradient red→green progress bar + "Step N of M" stage label + elapsed
 * clock + estimated-progress curve.
 *
 * Trimmed for the OBW step (as on the subscriber fork): no reassess and NO
 * completion-handoff hero — when the assessment decides, `PartnerStatusStep`
 * swaps THIS panel for the outcome view. Inputs are the applicant's website URL
 * + a started-at instant. Copy is staff-voice (the APPLICANT is emailed the
 * outcome, not the operator).
 */
import { useEffect, useState } from 'react';

// Typical agent runtime broken into 6 visual steps (~22s budget). Time-based —
// the agent doesn't stream intermediate state, so elapsed → step is mapped from
// cumulative durations. Mirrors the staff catalogue source exactly.
const PROGRESS_STAGES: Array<{ label: string; durationMs: number }> = [
  { label: 'Stage 1 — holistic classification',          durationMs: 4000 },
  { label: 'Portfolio authenticity — verifying clients', durationMs: 7000 },
  { label: 'Match quality — CN + token validation',      durationMs: 2000 },
  // ⚠ NOT "ABN / WHOIS". Corrected 17 September 2026 with the register wiring: this
  // stage is the identityAuthenticity VECTOR, and the label named the wrong things on
  // both counts. "ABN" is the AU identifier only — an NZ or US applicant watched the
  // portal say their ABN was being looked up. And WHOIS is not part of this vector at
  // all: `whois_lookup` feeds the portfolio longevity bonus. What actually runs here is
  // `verify_business_registration` (ABR / ACNC / NZBN / OpenSOSData) plus the
  // phone-on-website and address-on-website checks. Keep this jurisdiction-neutral —
  // the applicant watching it may be in any of the three.
  { label: 'Identity authenticity — business register + web signals', durationMs: 5000 },
  { label: 'Web presence breadth — LinkedIn + search',   durationMs: 3000 },
  { label: 'Finalising score + decision',                durationMs: 1000 },
];
const PROGRESS_TOTAL_MS = PROGRESS_STAGES.reduce((s, x) => s + x.durationMs, 0);

function progressFromElapsed(elapsedMs: number): { stepIdx: number; pct: number } {
  const cappedElapsed = Math.min(elapsedMs, PROGRESS_TOTAL_MS);
  let acc = 0;
  let stepIdx = PROGRESS_STAGES.length - 1;
  for (let i = 0; i < PROGRESS_STAGES.length; i++) {
    acc += PROGRESS_STAGES[i]!.durationMs;
    if (cappedElapsed < acc) { stepIdx = i; break; }
  }
  // Asymptotic curve so the bar never sits frozen at 95% when a real run runs
  // long (Anthropic latency + tool calls push runs to 45–90s). Linear to 80% by
  // the 22s budget, then an exponential approach to a 99% ceiling.
  const LINEAR_TARGET_PCT = 80;
  const ASYMPTOTE_PCT = 99;
  const TAIL_HALFLIFE_MS = 30000;
  let pct: number;
  if (elapsedMs <= PROGRESS_TOTAL_MS) {
    pct = (elapsedMs / PROGRESS_TOTAL_MS) * LINEAR_TARGET_PCT;
  } else {
    const overshoot = elapsedMs - PROGRESS_TOTAL_MS;
    const remaining = ASYMPTOTE_PCT - LINEAR_TARGET_PCT;
    pct = ASYMPTOTE_PCT - remaining * Math.pow(0.5, overshoot / TAIL_HALFLIFE_MS);
  }
  return { stepIdx, pct: Math.round(pct) };
}

export interface AssessmentInProgressProps {
  /** The applicant's website the agent is assessing. */
  websiteUrl: string;
  /** Wall-clock start for the progress bar (the submit instant, ISO). */
  startedAt: string;
}

export function AssessmentInProgress({ websiteUrl, startedAt }: AssessmentInProgressProps) {
  // Tick every 500ms so the bar + step animate smoothly.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 500);
    return () => clearInterval(id);
  }, []);

  const elapsedMs = Math.max(0, Date.now() - Date.parse(startedAt));
  const elapsedSec = Math.floor(elapsedMs / 1000);
  const elapsed = elapsedSec < 60
    ? `${elapsedSec}s`
    : `${Math.floor(elapsedSec / 60)}m ${elapsedSec % 60}s`;
  const isStillWorking = elapsedMs > PROGRESS_TOTAL_MS;
  const isLate = elapsedSec >= 90;

  const { stepIdx, pct } = progressFromElapsed(elapsedMs);
  const totalSteps = PROGRESS_STAGES.length;
  const currentStep = PROGRESS_STAGES[stepIdx]!;

  return (
    <section className="portal-assessment-progress" aria-live="polite">
      <h2>Assessment in progress</h2>
      <p className="portal-assessment-progress-url">
        Running the suitability assessment against{' '}
        <code>{websiteUrl || '—'}</code>
      </p>
      <div className="portal-assessment-progress-step" aria-live="polite">
        <span className="portal-assessment-progress-step-counter">
          Step {Math.min(stepIdx + 1, totalSteps)} of {totalSteps}
        </span>
        <span className="portal-assessment-progress-step-label">
          {isLate ? 'Finalising…' : currentStep.label}
        </span>
      </div>
      <div
        className="portal-assessment-progress-bar"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Estimated assessment progress: ${pct}%`}
        style={{ ['--progress' as string]: `${pct}%` }}
      >
        <div className="portal-assessment-progress-bar-fill" />
      </div>
      <div className={`portal-assessment-progress-meta portal-assessment-progress-meta-${elapsedSec < 15 ? 'fresh' : elapsedSec < 30 ? 'ontime' : elapsedSec < 60 ? 'warm' : elapsedSec < 90 ? 'late' : 'overdue'}`}>
        <strong>{elapsed}</strong> elapsed · typical run takes 20–60s
      </div>
      {isStillWorking && !isLate && (
        <div className="portal-assessment-progress-stillworking">
          <span className="portal-assessment-progress-stillworking-pulse" aria-hidden="true" />
          Still working — this can take 30–90s during peak load. Please keep this window open.
        </div>
      )}
      {isLate && (
        <div className="portal-assessment-progress-late">
          ⚠ Taking a little longer than typical. The applicant will be emailed the
          outcome if it outruns this window — the result also lands in the Partners queue.
        </div>
      )}
      <p className="portal-assessment-progress-footnote">
        Progress estimated from typical per-stage durations — the agent doesn't
        stream intermediate state. Auto-refresh every 3s.
      </p>
    </section>
  );
}
